// Resposta automática.

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as conta from "../api/conta";
import { explicar } from "../api/problema";
import { Aviso, Secao } from "./DoisFatores";
import { useNaoSalvo } from "./naoSalvo";

type Dados = NonNullable<Awaited<ReturnType<typeof conta.respostaAutomatica>>>;

const PADRAO: Dados = {
  ativa: false,
  assunto: "",
  mensagem: "",
  inicio: "",
  fim: "",
  intervalo_dias: 7,
};

export function Ferias() {
  const fila = useQueryClient();
  const [d, setD] = useState<Dados>(PADRAO);
  const [tocado, setTocado] = useState(false);
  const [erro, setErro] = useState("");
  const [pronto, setPronto] = useState(false);

  const atual = useQuery({
    queryKey: ["conta", "ferias"],
    queryFn: () => conta.respostaAutomatica(),
  });

  useEffect(() => {
    if (!tocado && atual.data) setD({ ...PADRAO, ...atual.data });
  }, [atual.data, tocado]);

  const salvar = useMutation({
    mutationFn: () => conta.salvarRespostaAutomatica(d),
    onSuccess: (salvo) => {
      setErro("");
      setPronto(true);
      // Ver Filtros.tsx.
      fila.setQueryData(["conta", "ferias"], salvo ?? d);
      setTocado(false);
    },
    onError: (e) => {
      setPronto(false);
      setErro(explicar(e));
    },
  });

  function muda<K extends keyof Dados>(chave: K, valor: Dados[K]) {
    setD((a) => ({ ...a, [chave]: valor }));
    setTocado(true);
    setPronto(false);
  }

  // O JSON é a comparação mais simples que não mente aqui: são seis campos
  // escalares, e a ordem das chaves é a mesma porque o objeto sai sempre do
  // mesmo literal (`PADRAO` espalhado).
  const naoSalvo = useNaoSalvo(
    "Resposta automática",
    JSON.stringify(d) !== JSON.stringify({ ...PADRAO, ...(atual.data ?? {}) }),
  );

  return (
    <Secao
      titulo="Resposta automática"
      icone="CalendarClock"
      estado={d.ativa ? "ativa" : undefined}
      naoSalvo={naoSalvo}
    >
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={!!d.ativa}
          onChange={(e) => muda("ativa", e.target.checked)}
        />
        Responder automaticamente quem me escrever
      </label>

      {/* OS CAMPOS APARECEM SEMPRE, não só com a caixinha marcada.
          Escondidos, a seção mostrava uma caixinha solta e um botão Salvar —
          e quem abre o /conta conclui que ela não foi implementada. Foi
          exatamente assim que o Jhou descreveu a tela em 28/10/2026.
          Mostrar o formulário é o que diz O QUE a seção faz; a caixinha diz
          se ele está valendo. */}
      <div
        className={"mt-4 grid gap-3" + (d.ativa ? "" : " opacity-60")}
        aria-hidden={false}
      >
        <div className="grid gap-3">
          <label className="text-sm">
            <span className="text-tinta-fraca">Assunto (opcional)</span>
            <input
              value={d.assunto ?? ""}
              onChange={(e) => muda("assunto", e.target.value)}
              placeholder="Fora do escritório"
              className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-sm"
            />
          </label>

          <label className="text-sm">
            <span className="text-tinta-fraca">Mensagem</span>
            <textarea
              value={d.mensagem ?? ""}
              onChange={(e) => muda("mensagem", e.target.value)}
              rows={5}
              maxLength={4000}
              className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-sm leading-relaxed"
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-sm">
              <span className="text-tinta-fraca">Começa em</span>
              <input
                type="date"
                value={d.inicio ?? ""}
                onChange={(e) => muda("inicio", e.target.value)}
                className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="text-tinta-fraca">Termina em</span>
              <input
                type="date"
                value={d.fim ?? ""}
                onChange={(e) => muda("fim", e.target.value)}
                className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="text-tinta-fraca">Repetir a cada</span>
              <select
                value={d.intervalo_dias ?? 7}
                onChange={(e) => muda("intervalo_dias", Number(e.target.value))}
                className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-sm"
              >
                {[1, 3, 5, 7, 15, 30].map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? "dia" : "dias"}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <Aviso tom="neutro">
            <strong>Termina em</strong> é o que evita o clássico “voltei das
            férias há três semanas e ainda respondo que estou fora”. E{" "}
            <strong>repetir a cada</strong> vale por remetente: responder a
            cada mensagem transforma uma conversa em dois robôs.
          </Aviso>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => salvar.mutate()}
          disabled={salvar.isPending}
          className="rounded-lg bg-acao px-4 py-2 text-sm text-white disabled:opacity-60"
        >
          {salvar.isPending ? "…" : "Salvar"}
        </button>
        {pronto && <span className="text-sm text-acao">Salva.</span>}
      </div>
      {erro && <p className="mt-3 text-sm text-perigo">{erro}</p>}
    </Secao>
  );
}
